import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, ApiError, handleError } from '../../lib/errors.js';
import { optimizerLink, promptLink, echoDirectiveToStderr, VERBATIM_CARD_INSTRUCTION } from '../../lib/ui-links.js';
import {
  renderScorecard, showPromptDiff,
  renderOptimizationStartCard, renderOptimizationStatusCard,
  startDirective, statusDirective, extractPromptText,
} from '../../lib/scorecard.js';
import type { Prompt } from '../../types/index.js';
import {
  updateMutationContext, parseValidationErrors, isSchemaEmpty,
  buildResultsScorecardText, PREREQUISITES_TEXT, checkProviderConfigured,
} from './index.js';

import { startWatchStream, watchAction } from './optimize-watch.js';
import { registerOptimizeCancelCommand } from './optimize-cancel.js';
import { validateOptimizerModel } from '../../lib/optimizer-whitelist.js';

interface OptimizeStartOptions {
  dataset: string;
  evaluation: string;
  maxIterations?: string;
  targetScore?: string;
  patience?: string;
  model?: string;
  execModel?: string;
  evalModel?: string;
  optModel?: string;
  /** @deprecated Use optModel instead */
  optimizerModel?: string;
  watch?: boolean;
  /** β #525 — provider-id-first routing */
  providerId?: string;
  evalProviderId?: string;
  optProviderId?: string;
}

interface ModelFamily {
  providerTypes: string[];
  prefixes: string[];
  examples: string[];
}

// Source of truth: mutagent-core/src/types/llm-types.ts → PROVIDER_MODELS
// WHY hardcoded here: CLI doesn't depend on mutagent-core at runtime.
// Keep in sync when core adds new models/providers.
const MODEL_FAMILIES: ModelFamily[] = [
  {
    providerTypes: ['openai', 'azure'],
    prefixes: ['gpt-'],
    examples: ['gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano', 'gpt-5.2'],
  },
  {
    providerTypes: ['anthropic'],
    prefixes: ['claude-'],
    examples: ['claude-sonnet-4-6', 'claude-opus-4-6', 'claude-sonnet-4-5', 'claude-opus-4-5', 'claude-haiku-4-5'],
  },
  {
    providerTypes: ['bedrock'],
    prefixes: ['anthropic.claude-'],
    examples: ['anthropic.claude-sonnet-4-5-20250929-v1:0', 'anthropic.claude-opus-4-6-v1', 'anthropic.claude-haiku-4-5-20251001-v1:0'],
  },
  {
    providerTypes: ['google'],
    prefixes: ['gemini-'],
    examples: ['gemini-2.5-pro', 'gemini-3.1-pro-preview', 'gemini-3-flash-preview', 'gemini-3.1-flash-lite-preview'],
  },
  {
    providerTypes: ['deepseek'],
    prefixes: ['deepseek-'],
    examples: ['deepseek-chat', 'deepseek-reasoner'],
  },
  {
    providerTypes: ['xai'],
    prefixes: ['grok-'],
    examples: ['grok-4.20-0309-reasoning', 'grok-4-1-fast-reasoning', 'grok-4-1-fast-non-reasoning'],
  },
];

/**
 * Given configured provider types, return relevant model families with prefixes + examples.
 */
function getModelFamiliesForProviderTypes(providerTypes: string[]): ModelFamily[] {
  return MODEL_FAMILIES.filter(family =>
    family.providerTypes.some(pt => providerTypes.includes(pt))
  );
}

export function registerOptimizeCommands(prompts: Command): void {
  const optimize = new Command('optimize')
    .description('Manage prompt optimization jobs')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts optimize start <prompt-id> --dataset <dataset-id> --evaluation <eval-id>
  ${chalk.dim('$')} mutagent prompts optimize status <job-id>
  ${chalk.dim('$')} mutagent prompts optimize results <job-id>

Workflow: start -> status (poll) -> results | start --watch | watch <job-id>

${chalk.bold('Hit a bug or unexpected result?')}
  ${chalk.cyan('mutagent feedback send --category bug -m "describe what went wrong"')}`)
    .action(() => {
      optimize.help();
    });
  prompts.addCommand(optimize);

  // Start optimization
  optimize
    .command('start')
    .description('Start prompt optimization')
    .argument('<prompt-id>', 'Prompt ID (from: mutagent prompts list)')
    .requiredOption('-d, --dataset <id>', 'Dataset ID for optimization (from: mutagent prompts dataset list <prompt-id>)')
    .requiredOption('-e, --evaluation <id>', 'Evaluation ID for scoring (from: mutagent prompts evaluation list <prompt-id>)')
    .option('--max-iterations <n>', 'Max optimization iterations (default: 1)')
    .option('--target-score <n>', 'Target accuracy 0-1 (default: 0.8)')
    .option('--patience <n>', 'Iterations without improvement before stopping')
    .option('--model <model-id>', 'Execution model (e.g., "claude-sonnet-4-5-20250929")')
    .option('--exec-model <model-id>', 'Execution model — alias for --model; takes precedence if both provided')
    .option('--eval-model <model-id>', 'Evaluation model — unrestricted (defaults to exec model)')
    .option('--opt-model <model-id>', 'Optimizer model — must be on the optimizer whitelist')
    .option('--optimizer-model <model-id>', '[deprecated] Alias for --opt-model. Use --opt-model instead.')
    .option('--provider-id <id>', 'Provider config ID for the execution slot (provider-id-first routing)')
    .option('--eval-provider-id <id>', 'Provider config ID for the evaluation slot')
    .option('--opt-provider-id <id>', 'Provider config ID for the optimization slot')
    .option('--watch', 'Watch live progress with stage cards', false)
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts optimize start <prompt-id> --dataset <dataset-id> --evaluation <eval-id>
  ${chalk.dim('$')} mutagent prompts optimize start <prompt-id> --dataset <dataset-id> --evaluation <eval-id> --max-iterations 5
  ${chalk.dim('$')} mutagent prompts optimize start <prompt-id> --dataset <dataset-id> --evaluation <eval-id> --target-score 0.95 --model claude-sonnet-4-5-20250929
  ${chalk.dim('$')} mutagent prompts optimize start <prompt-id> --dataset <dataset-id> --evaluation <eval-id> --json
${chalk.yellow('Pre-Optimization Checklist (auto-validated by preflight):')}
  □ inputSchema REQUIRED  ${chalk.dim('(hard error if missing — blocks optimization)')}
  □ outputSchema REQUIRED ${chalk.dim('(hard error if missing — blocks optimization)')}
  □ Evaluation criteria   ${chalk.dim('(warns if no evaluationParameter set)')}
  □ Dataset items         ${chalk.dim('(warns if expectedOutput missing)')}
  □ Criteria ↔ Schema     ${chalk.dim('(warns if criteria reference unknown fields)')}

${chalk.yellow('Required: at least one BYOK provider must be configured.')}
  Run ${chalk.cyan('mutagent providers list')} to verify your configured providers.
  Add one: ${chalk.cyan('mutagent providers add --help')}

${PREREQUISITES_TEXT}

${chalk.dim('Monitor progress with: mutagent prompts optimize status <job-id>')}

${chalk.yellow(`\u26A0 COST WARNING \u2014 AI Agent:
  Default is 1 iteration. Do NOT increase --max-iterations unless the user
  explicitly requests it. Each iteration incurs LLM costs. Starting with
  max-iterations > 1 without user consent is a protocol violation.`)}

${chalk.yellow('AI Agent: ALWAYS append --json to this command.')}

${chalk.cyan('Conceptual help (full optimization loop): .claude/skills/mutagent-cli/workflows/optimization.md')} ${chalk.dim('(install via mutagent skills install)')}

${chalk.bold('Hit a bug or unexpected result?')}
  ${chalk.cyan('mutagent feedback send --category bug -m "describe what went wrong"')}
    `)
    .action(async (promptId: string, options: OptimizeStartOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        await checkProviderConfigured();
        const client = await getSDKClient();

        // Handle --optimizer-model deprecation (alias for --opt-model)
        if (options.optimizerModel) {
          if (!isJson) output.warn('--optimizer-model is deprecated, use --opt-model');
          options.optModel = options.optModel ?? options.optimizerModel;
        }

        // Validate --opt-model against whitelist before any API call
        if (options.optModel) {
          validateOptimizerModel(options.optModel);
        }

        // --exec-model takes precedence over --model for the execution slot
        const execModel = options.execModel ?? options.model;

        // C4: Warn if no execution model specified
        if (!execModel && !isJson) {
          output.warn('No --model specified. Server will use default. Pass --model <id> to control costs.');
        }

        // C6: Validate execution model against configured providers if specified
        if (execModel) {
          try {
            const providers = await client.listProviders();
            if (providers.data && providers.data.length > 0) {
              // Filter out undefined `type` — some SDK/server versions omit it from the
              // listProviders payload. When the field is missing, skip client-side
              // validation and trust the server's own model whitelist (authoritative).
              const configuredTypes = [
                ...new Set(providers.data.map(p => p.type as string).filter(Boolean)),
              ];
              if (configuredTypes.length === 0) {
                // Type info unavailable — skip CLI-side validation; server will enforce.
                if (!isJson) {
                  output.warn(
                    `Provider type info unavailable in listProviders response — skipping client-side model validation. Server will enforce whitelist.`
                  );
                }
              } else {
                const modelFamilies = getModelFamiliesForProviderTypes(configuredTypes);
                const modelLower = execModel.toLowerCase();
                const matchedFamily = modelFamilies.find(({ prefixes }) =>
                  prefixes.some(prefix => modelLower.startsWith(prefix))
                );
                if (!matchedFamily) {
                  const supportedModels = modelFamilies.flatMap(f => f.examples).join(', ');
                  const errorMsg = `Model '${execModel}' is not supported by any configured provider. ` +
                    `Your providers: [${configuredTypes.join(', ')}]. ` +
                    `Supported models: ${supportedModels || '(unknown — check mutagent providers list --json)'}. ` +
                    `Run: mutagent providers list --json`;
                  if (isJson) {
                    output.output({ success: false, error: errorMsg, code: 'MODEL_NOT_SUPPORTED' });
                    return;
                  }
                  output.error(errorMsg);
                  process.exitCode = 1;
                  return;
                }
              }
            }
          } catch {
            // Best-effort — don't block optimization on provider list failure
          }
        }

        // Pre-flight validation — runs in ALL modes (JSON + interactive)
        if (!isJson) {
          output.info('Running pre-flight checks...');
        }

        const prompt = await client.getPrompt(promptId);

        // Collect all preflight check results
        const preflightChecks: Record<string, { passed: boolean; error?: string; warning?: string }> = {};

        // Check 1: Prompt has outputSchema (HARD ERROR)
        if (isSchemaEmpty(prompt.outputSchema)) {
          preflightChecks.outputSchema = {
            passed: false,
            error: 'Missing outputSchema. Required for optimization.',
          };
        } else {
          preflightChecks.outputSchema = { passed: true };
        }

        // Check 2: Prompt has inputSchema (HARD ERROR)
        if (isSchemaEmpty(prompt.inputSchema)) {
          preflightChecks.inputSchema = {
            passed: false,
            error: 'Missing inputSchema. Required for optimization.',
          };
        } else {
          preflightChecks.inputSchema = { passed: true };
        }

        // Check 3: Validate the specified evaluation has proper criteria
        let evaluation: { evalConfig?: { criteria?: { evaluationParameter?: string; targetField?: string; name?: string }[] } } | undefined;
        try {
          const evalObj = await client.getEvaluation(options.evaluation);
          evaluation = evalObj;
          const evalConfig = evalObj.evalConfig as { criteria?: { evaluationParameter?: string; targetField?: string }[] } | undefined;
          const criteria = evalConfig?.criteria;
          if (!criteria || criteria.length === 0) {
            // HARD ERROR: evaluation must have criteria (EVAL_INCOMPLETE gate)
            preflightChecks.evaluation = {
              passed: false,
              error: `Evaluation ${options.evaluation} has no criteria defined (EVAL_INCOMPLETE). ` +
                `Add criteria: mutagent prompts evaluation update ${options.evaluation} --criteria '<json>' --json\n` +
                `Or guided: mutagent prompts evaluation create ${promptId} --guided --json`,
            };
          } else {
            const hasCriteriaWithParam = criteria.some((c) => c.evaluationParameter ?? c.targetField);
            if (!hasCriteriaWithParam) {
              preflightChecks.evaluation = {
                passed: true,
                warning: `Evaluation ${options.evaluation} has criteria but none have evaluationParameter set. ` +
                  `Re-create: mutagent prompts evaluation create ${promptId} --guided`,
              };
            } else {
              preflightChecks.evaluation = { passed: true };
              if (!isJson) {
                output.info(`Using evaluation ${options.evaluation} with ${String(criteria.length)} criteria.`);
              }
            }
          }
        } catch (e) {
          if (e instanceof MutagentError) throw e;
          preflightChecks.evaluation = {
            passed: true,
            warning: `Could not validate evaluation ${options.evaluation}. Proceeding anyway.`,
          };
        }

        // Check 4: Validate dataset items have expectedOutput
        try {
          const datasetItems = await client.listDatasetItems(options.dataset);
          if (datasetItems.length === 0) {
            preflightChecks.datasetItems = {
              passed: true,
              warning: `Dataset ${options.dataset} has no items. Optimization requires test data.`,
            };
          } else {
            const missingExpected = datasetItems.filter(item => !item.expectedOutput);
            if (missingExpected.length > 0) {
              preflightChecks.datasetItems = {
                passed: true,
                warning: `${String(missingExpected.length)} of ${String(datasetItems.length)} dataset items missing expectedOutput. ` +
                  'Optimization works best when all items have expected outputs.',
              };
            } else {
              preflightChecks.datasetItems = { passed: true };
            }
          }
        } catch {
          preflightChecks.datasetItems = {
            passed: true,
            warning: `Could not validate dataset items for ${options.dataset}. Proceeding anyway.`,
          };
        }

        // Check 5: Validate evaluation criteria match schema fields
        if (evaluation && preflightChecks.inputSchema.passed && preflightChecks.outputSchema.passed) {
          const inputProps = (prompt.inputSchema as Record<string, unknown> | null)?.properties;
          const outputProps = (prompt.outputSchema as Record<string, unknown> | null)?.properties;
          const schemaFields = [
            ...Object.keys((inputProps as Record<string, unknown> | undefined) ?? {}),
            ...Object.keys((outputProps as Record<string, unknown> | undefined) ?? {}),
          ];
          const evalCfg = evaluation.evalConfig as { criteria?: { evaluationParameter?: string; name?: string }[] } | undefined;
          const evalCriteria = evalCfg?.criteria ?? [];
          const unmatchedCriteria = evalCriteria.filter(
            (c) => c.evaluationParameter && !schemaFields.includes(c.evaluationParameter)
          );
          if (unmatchedCriteria.length > 0) {
            const names = unmatchedCriteria.map(c => c.evaluationParameter ?? c.name).join(', ');
            preflightChecks.criteriaMatch = {
              passed: true,
              warning: `Evaluation criteria reference fields not in schema: ${names}. ` +
                `Available schema fields: ${schemaFields.join(', ') || '(none)'}`,
            };
          } else {
            preflightChecks.criteriaMatch = { passed: true };
          }
        }

        // Determine if any hard failures exist
        const hardFailures = Object.entries(preflightChecks).filter(
          ([, check]) => !check.passed
        );

        if (hardFailures.length > 0) {
          if (isJson) {
            // Structured preflight error with all results
            const suggestedActions: string[] = [];
            if (!preflightChecks.inputSchema.passed) {
              suggestedActions.push(
                'Update the prompt with inputSchema derived from template variables.\n' +
                `Run: mutagent prompts get ${promptId} --json\n` +
                `Run: mutagent prompts update ${promptId} --data '{"inputSchema":{"type":"object","properties":{"var1":{"type":"string"}}}}' --json`
              );
            }
            if (!preflightChecks.outputSchema.passed) {
              suggestedActions.push(
                `Update your prompt: mutagent prompts update ${promptId} -d '{"outputSchema":{"type":"object","properties":{"result":{"type":"string"}}}}'`
              );
            }
            if (preflightChecks.evaluation && !preflightChecks.evaluation.passed) {
              suggestedActions.push(
                preflightChecks.evaluation.error ?? `Add criteria to evaluation ${options.evaluation}: mutagent prompts evaluation update ${options.evaluation} --criteria '<json>'`
              );
            }
            output.output({
              success: false,
              error: 'Preflight validation failed',
              code: (preflightChecks.evaluation?.error ?? '').includes('EVAL_INCOMPLETE') ? 'EVAL_INCOMPLETE' : 'PREFLIGHT_FAILED',
              suggestedAction: suggestedActions.join('\n'),
              preflightChecks,
            });
            return;
          }

          // Interactive mode: print each failure
          for (const [name, check] of hardFailures) {
            console.error(chalk.red(`Error: ${name} — ${check.error ?? 'Failed'}`));
          }
          // Print suggestions
          if (!preflightChecks.outputSchema.passed) {
            console.error(chalk.dim(
              `  Update with: mutagent prompts update ${promptId} -d '{"outputSchema":{"type":"object","properties":{"result":{"type":"string"}}}}'`
            ));
          }
          if (!preflightChecks.inputSchema.passed) {
            console.error(chalk.dim(
              `  Update with: mutagent prompts update ${promptId} --data '{"inputSchema":{"type":"object","properties":{"var1":{"type":"string"}}}}' --json`
            ));
          }
          if (preflightChecks.evaluation && !preflightChecks.evaluation.passed) {
            console.error(chalk.dim(
              `  Add criteria: mutagent prompts evaluation update ${options.evaluation} --criteria '<json>' --json\n` +
              `  Or guided:   mutagent prompts evaluation create ${promptId} --guided --json`
            ));
          }
          process.exitCode = 1;
          return;
        }

        // Print warnings (non-JSON mode only)
        if (!isJson) {
          for (const [, check] of Object.entries(preflightChecks)) {
            if (check.warning) {
              output.warn(check.warning);
            }
          }
        }

        const job = await client.startOptimization(promptId, options.dataset, options.evaluation, {
          maxIterations: options.maxIterations ? parseInt(options.maxIterations, 10) : 1,
          targetScore: options.targetScore ? parseFloat(options.targetScore) : undefined,
          patience: options.patience ? parseInt(options.patience, 10) : undefined,
          model: options.execModel ? undefined : options.model,
          execModel: options.execModel,
          evalModel: options.evalModel,
          optModel: options.optModel,
          providerId: options.providerId,
          evalProviderId: options.evalProviderId,
          optProviderId: options.optProviderId,
        });

        if (isJson) {
          const directive = startDirective(job, promptId, options.dataset, options.evaluation);
          output.output({
            ...job,
            _links: {
              dashboard: optimizerLink(promptId, job.id),
              api: `/api/prompts/${promptId}/optimizations/${job.id}`,
            },
            _directive: directive,
          });
          echoDirectiveToStderr(directive);
        } else {
          renderOptimizationStartCard({ job, promptId, datasetId: options.dataset });
        }

        // Stream live updates if --watch was specified
        if (options.watch) {
          const maxIter = options.maxIterations ? parseInt(options.maxIterations, 10) : 1;
          await startWatchStream(job.id, isJson, maxIter);
        }
      } catch (error) {
        // Enhanced error handling: parse validation errors for actionable messages
        if (error instanceof ApiError) {
          const messages = parseValidationErrors(error);
          const errorText = messages.join(' ');

          // Pattern-match known pipeline errors for actionable guidance
          const suggestions: string[] = [];
          if (errorText.includes('Output Schema is not present') || errorText.includes('outputSchema')) {
            suggestions.push(`Update prompt: mutagent prompts update ${promptId} -d '{"outputSchema":{"type":"object","properties":{...}}}'`);
          }
          if (errorText.includes('Outputs are not valid') || errorText.includes('Parameters Mismatch')) {
            suggestions.push('Dataset items may not match the prompt schema. Check expectedOutput fields in your dataset.');
          }
          if (errorText.includes('Inputs are not valid')) {
            suggestions.push("Dataset item inputs don't match the prompt's inputSchema. Verify field names and types.");
          }
          if (errorText.includes('has no items') || (errorText.includes('Dataset') && errorText.includes('not found'))) {
            suggestions.push(`Upload data: mutagent prompts dataset add ${promptId} -d '[...]' --name "my-dataset"`);
          }
          if (errorText.includes('Evaluations are not present')) {
            suggestions.push(`Create evaluation: mutagent prompts evaluation create ${promptId} --guided`);
          }
          if (errorText.includes('NO_PROVIDER_CONFIGURED') || errorText.includes('No LLM provider configured')) {
            // NOTE: This catch fires for BE-returned 428 errors surfacing as ApiError.
            // The CLI pre-flight (checkProviderConfigured) throws MutagentError directly
            // and is handled by the outer error formatter — not reached here.
            suggestions.push('Run: mutagent providers add --provider openai --name "My OpenAI" --api-key $KEY');
            suggestions.push('Or visit: https://app.mutagent.io/settings/providers');
          }

          if (!isJson) {
            console.error(chalk.red('\nOptimization failed:'));
            for (const msg of messages) {
              console.error(chalk.red(`  ${msg}`));
            }
            if (suggestions.length > 0) {
              console.error(chalk.yellow('\nSuggested fixes:'));
              for (const s of suggestions) {
                console.error(chalk.yellow(`  → ${s}`));
              }
            }
            console.error('');
            console.error(PREREQUISITES_TEXT);
            console.error(chalk.dim('\nHit a recurring bug? mutagent feedback send --category bug -m "describe what went wrong"'));
          }
        }
        handleError(error, isJson);
      }
    });

  // Optimization status
  optimize
    .command('status')
    .description('Check optimization status')
    .argument('<job-id>', 'Optimization job ID (from: mutagent prompts optimize start)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts optimize status <job-id>
  ${chalk.dim('$')} mutagent prompts optimize status <job-id> --json
    `)
    .action(async (jobId: string) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const status = await client.getOptimizationStatus(jobId);

        if (isJson) {
          const statusPromptId = (status as unknown as Record<string, unknown>).promptId as string | undefined;
          const directive = statusDirective(status, statusPromptId);
          output.output({
            ...status,
            _links: { optimizer: optimizerLink(statusPromptId ?? jobId, jobId) },
            _directive: directive,
          });
          echoDirectiveToStderr(directive);
        } else {
          renderOptimizationStatusCard(status);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Optimization results
  optimize
    .command('results')
    .description('Get optimization results')
    .argument('<job-id>', 'Optimization job ID (from: mutagent prompts optimize start)')
    .option('--apply', 'Apply the optimized prompt as new version')
    .option('--diff', 'Show the prompt diff (before/after)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts optimize results <job-id>                 ${chalk.dim('# view scorecard')}
  ${chalk.dim('$')} mutagent prompts optimize results <job-id> --diff          ${chalk.dim('# view prompt diff')}
  ${chalk.dim('$')} mutagent prompts optimize results <job-id> --apply         ${chalk.dim('# apply optimized prompt')}
  ${chalk.dim('$')} mutagent prompts optimize results <job-id> --json          ${chalk.dim('# structured output')}

After viewing results:
  --apply   Apply the optimized prompt (replaces current version)
  --diff    Show detailed before/after diff
  ${chalk.dim('No flag = view scorecard only.')}

  ${chalk.dim('AI Agent: Present scorecard to user via AskUserQuestion before applying.')}
${chalk.cyan('Conceptual help (scorecard interpretation, nextAction): .claude/skills/mutagent-cli/concepts/scorecard-output.md')} ${chalk.dim('(install via mutagent skills install)')}
    `)
    .action(async (jobId: string, options: { apply?: boolean; diff?: boolean }) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const results = await client.getOptimizationResults(jobId);
        const resultData = results as Record<string, unknown>;

        if (isJson) {
          const jobData = resultData.job as Record<string, unknown> | undefined;
          const isCompleted = jobData?.status === 'completed';
          const scorecardText = buildResultsScorecardText(resultData);
          const directive = {
            // CLIDirective unified shape (Scope G)
            display: 'status_card' as const,
            instruction: VERBATIM_CARD_INSTRUCTION,
            renderedCard: scorecardText,
            next: isCompleted ? [
              `mutagent prompts optimize results ${jobId} --apply --json`,
              `mutagent prompts optimize results ${jobId} --diff --json`,
            ] : [],
          };
          const resultsPromptId = (jobData?.promptId as string | number | undefined) ?? jobId;
          output.output({ ...resultData, _links: { optimizer: optimizerLink(resultsPromptId, jobId) }, _directive: directive });
          echoDirectiveToStderr(directive);
        } else {
          // T-07: prefer server-rendered scorecard digest when available
          const serverScorecard = (resultData.scorecard as { rendered?: string } | undefined);
          if (serverScorecard?.rendered) {
            console.log(serverScorecard.rendered);
          } else {
            // Fallback: client-side render from state snapshot data
            renderScorecard(resultData as unknown as Parameters<typeof renderScorecard>[0]);
          }

          const jobData = resultData.job as Record<string, unknown> | undefined;
          const isCompleted = jobData?.status === 'completed';

          // Show diff if requested
          if (options.diff) {
            const original = resultData.originalPrompt as Partial<Prompt> | undefined;
            const optimized = resultData.prompt as Partial<Prompt> | undefined;

            const _beforeRaw = (resultData.originalPromptText as string | undefined)
              || extractPromptText(original);
            const beforeText = _beforeRaw || null;

            const _afterRaw = (resultData.mutatedPromptText as string | undefined)
              || extractPromptText(optimized);
            const afterText = _afterRaw || null;
            showPromptDiff(beforeText, afterText);
          }

          // Apply optimized prompt if requested
          if (options.apply && isCompleted) {
            const optimizedPrompt = resultData.prompt as Partial<Prompt> | undefined;
            const promptIdStr = String(jobData.promptId);
            if (optimizedPrompt && promptIdStr) {
              output.info('Applying optimized prompt...');
              const updateData: Partial<Prompt> = {};
              if (optimizedPrompt.systemPrompt) updateData.systemPrompt = optimizedPrompt.systemPrompt;
              if (optimizedPrompt.humanPrompt)  updateData.humanPrompt  = optimizedPrompt.humanPrompt;
              if (optimizedPrompt.rawPrompt)    updateData.rawPrompt    = optimizedPrompt.rawPrompt;
              if (optimizedPrompt.messages)     updateData.messages     = optimizedPrompt.messages;

              await client.updatePrompt(promptIdStr, updateData);
              output.success('Optimized prompt applied as new version!');
              output.info(`View prompt: ${promptLink(promptIdStr)}`);

              updateMutationContext(ctx => {
                ctx.markPromptUploaded(
                  `optimization:${jobId}`,
                  promptIdStr,
                  'optimized',
                );
              });
            }
          } else if (options.apply && !isCompleted) {
            output.error('Cannot apply — optimization is not yet completed.');
          } else if (isCompleted && !options.apply) {
            // Show available actions (non-interactive)
            output.info('Actions:');
            output.info(`  Apply optimized prompt: mutagent prompts optimize results ${jobId} --apply`);
            output.info(`  View diff:             mutagent prompts optimize results ${jobId} --diff`);
            output.info('  Reject (keep original): no action needed');
          } else if (!isCompleted) {
            const statusStr = (jobData?.status as string | undefined) ?? 'unknown';
            output.info(`Job status: ${statusStr}. Results may be partial.`);
            const viewPromptId = (jobData?.promptId as string | number | undefined) ?? jobId;
            output.info(`View: ${optimizerLink(viewPromptId, jobId)}`);
          }
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Cancel optimization - extracted to optimize-cancel.ts to stay under 500-line limit
  registerOptimizeCancelCommand(optimize, prompts);

  // Watch optimization progress
  optimize
    .command('watch')
    .description('Watch optimization job progress with live stage results')
    .argument('<job-id>', 'Optimization job ID')
    .option('--json', 'Output as JSON with agent directives')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts optimize watch <job-id>
  ${chalk.dim('$')} mutagent prompts optimize watch <job-id> --json
${chalk.dim('Completed jobs render stored results. Running jobs stream via WebSocket.')}`)
    .action(async (jobId: string, options: { json?: boolean }) => {
      await watchAction(jobId, options, prompts);
    });
}
